import { faCopy, faInfo } from '@fortawesome/free-solid-svg-icons'
import { faGear } from '@fortawesome/pro-solid-svg-icons'
import useAppView from 'Core/Hooks/useAppView'
import useMultiSlice from 'Core/Hooks/useMultiSlice'
import { useUser } from 'Core/Hooks/useUser'
import { enqueueNotification, getAccountIcon } from 'Utils/Helpers'
import { FullScreenAlertModal, LabelContent, LoadingFeedback } from 'V2Components'
import { ErrorLabelContent, getResourceName, getRsrcIcon } from 'features/resources'
import { updateRDPServer, useRDPAccess } from 'features/targets'
import { reduxApiClient } from 'infra'
import _ from 'lodash'
import { Button, IconButton, Label, TextInput, Tooltip, Typography } from 'procyon-ui'
import React, { useEffect, useState } from 'react'
import { RDPADDCSetupModal } from '../../../../../RDPADDCSetupModal'
import { MeshClusterLabelContent } from '../MeshClusterLabelContent'
import { getHTMLDataTestID } from 'infra/utils'

const RDPServer = ({ rsrc, account }) => {
  const { appView } = useAppView()
  const { user } = useUser()
  const { getUserRDPAccessLinks } = useRDPAccess()
  const [isLoading, setIsLoading] = useState(true)
  const [showADDCSetupModal, setShowADDCSetupModal] = useState(false)
  const { getObjectRef } = useMultiSlice(['adDomainControllers'])
  const AccountIcon = getAccountIcon(_.get(account, 'Spec.Type', ''.toLowerCase()))
  const isAdDomainSetupExist = rsrc.Spec.ADDomainController.RefID === '0' ? false : true
  const addc = getObjectRef(rsrc.Spec.ADDomainController)
  const cloudVM = getObjectRef(rsrc.Spec.CloudVM)
  const CloudVMIcon = getRsrcIcon(cloudVM)

  useEffect(() => {
    getCurrentUserCredentails(rsrc)
  }, [rsrc])

  // get policy for user associated with this rdp rsrc
  const isPolicyExists = Object.keys(getUserRDPAccessLinks(user, rsrc)).length > 0
  const getCurrentUserCredentails = (data) => {
    const userMap = data?.Spec?.UserMap?.Map || {}
    const credentialsMap = data?.Spec?.Credentials?.Map || {}
    const matchedUser = Object.entries(userMap).filter(
      ([key, value]) => value.RefID === user.ObjectMeta.ID
    )
    const matchedKeys = new Set(matchedUser.map((item) => item[0]))
    const associatedCreds = Object.keys(credentialsMap).filter((key) => userMap.hasOwnProperty(key))
    const nonAssociatedCreds = Object.keys(credentialsMap).filter(
      (key) => !userMap.hasOwnProperty(key)
    )
    const matchedCredentials = associatedCreds.filter((key) => matchedKeys.has(key))
    const combinedArray = [...matchedCredentials, ...nonAssociatedCreds]
    const filteredCreds = combinedArray.reduce((acc, key) => {
      acc[key] = ''
      return acc
    }, {})

    if (filteredCreds) {
      return filteredCreds
    } else {
      return {}
    }
  }
  let credentialsData = getCurrentUserCredentails(rsrc)

  let initialRows = Object.keys(credentialsData).map((username) => ({
    Username: username,
    Password: credentialsData[username],
    mandatory: false,
    error: false,
    disableInput: true
  }))

  const [dbRow, setDBrow] = useState(initialRows)

  const handleCopyCmd = async (item) => {
    const { Tenant, Namespace, Name } = rsrc.ObjectMeta
    const payloadObj = {
      ObjectMeta: {
        Tenant: Tenant,
        NameSpace: Namespace,
        Name: Name
      },
      UserName: item
    }
    try {
      const response = await reduxApiClient('rdppasswords').create(payloadObj)
      console.log(response.Password)
      navigator.clipboard.writeText(response?.Password)
      enqueueNotification('Password Copied SuccessFully !', 'info')
    } catch (error) {
      console.log(error)
    }
  }

  const getDNSName = (url) => {
    if (user.org !== 'default') {
      return (
        url?.replace('.rdp.procyon.ai', `-rdp-${user.org}.applications.${user.controller}`) || ''
      )
    } else {
      return url?.replace('.rdp.procyon.ai', `-rdp.applications.${user.controller}`) || ''
    }
  }

  const Icon = getRsrcIcon(addc)

  useEffect(() => {
    if (rsrc) {
      setIsLoading(false)
    }
    const userPolicy = getUserRDPAccessLinks(user, rsrc)
    reduxApiClient('rdpservers').getByDataParams({ 'ObjectMeta.Name': rsrc.ObjectMeta.Name })
    let updatedInitialRow
    if (appView === 'user' && userPolicy) {
      updatedInitialRow = initialRows?.filter((item) => userPolicy[item.Username])
    } else {
      updatedInitialRow = initialRows
    }
    setDBrow(updatedInitialRow)
  }, [rsrc])

  return (
    <>
      <ErrorLabelContent rsrc={rsrc} />
      <LabelContent
        data-testid={'name'}
        dataTestId={getHTMLDataTestID('rdp', 'label', 'name')}
        title='Name'
        content={getResourceName(rsrc)}
      />
      <LabelContent
        data-testid={'account'}
        dataTestId={getHTMLDataTestID('rdp', 'label', 'account')}
        title='Account'
        content={AccountIcon && <Label icon={<AccountIcon />} text={getResourceName(account)} />}
      />
      <LabelContent
        data-testid={'cloud-vm'}
        dataTestId={getHTMLDataTestID('rdp', 'label', 'cloud-vm')}
        title='Cloud VM'
        content={CloudVMIcon && <Label text={getResourceName(rsrc)} icon={<CloudVMIcon />} />}
      />
      <LabelContent
        data-testid={'ip-address'}
        title={'IP Address'}
        dataTestId={getHTMLDataTestID('rdp', 'label', 'ip-address')}
        content={rsrc.Spec.Hostname}
      />
      <LabelContent
        data-testid={'fqdn'}
        dataTestId={getHTMLDataTestID('rdp', 'label', 'fqdn')}
        title={'FQDN'}
        content={rsrc.Status.FQDN}
      />
      {appView === 'admin' && (
        <LabelContent
          data-testid={'addomain-controller'}
          dataTestId={getHTMLDataTestID('rdp', 'label', 'addc')}
          title='RDP User Management'
          content={
            <div className='flex justify-between items-center gap-4'>
              {addc && <Label icon={<Icon />} text={getResourceName(addc)} />}
              {!addc && <Typography variant='body-regular'>No RDP User Config</Typography>}
              <Button icon={faGear} onClick={() => setShowADDCSetupModal(true)} variant='primary'>
                Setup
              </Button>
            </div>
          }
        />
      )}
      {showADDCSetupModal && (
        <RDPADDCSetupModal
          onCancel={() => setShowADDCSetupModal(false)}
          onSuccess={() => setShowADDCSetupModal(false)}
          rdpServer={rsrc}
          account={account}
        />
      )}
      <LabelContent
        data-testid={'domain-name'}
        dataTestId={getHTMLDataTestID('rdp', 'label', 'domain-name')}
        title={'Domain Name'}
        content={
          <Typography variant='body-regular'>
            {getDNSName(rsrc?.Spec?.DNSNames?.Elems[0])}
          </Typography>
        }
      />

      {appView === 'admin' && <MeshClusterLabelContent rsrc={rsrc} />}

      {isAdDomainSetupExist && (
        <Typography className='mt-4 mb-4 !text-gray-500' variant='body-regular'>
          Use <strong>user@domain</strong> for domain control
        </Typography>
      )}
      {isAdDomainSetupExist &&
        dbRow.length > 0 &&
        dbRow.map((e, i) => {
          return (
            <>
              {!dbRow[i]?.Username.includes('placeholder') && (
                <div
                  data-testid={getHTMLDataTestID('rdp', 'label', 'add-user-aws')}
                  className='flex gap-12 border-b-1 mt-2 mb-2 pb-2'
                  style={{ borderBottom: '1px solid #D8DDE4' }}
                  key={i}
                >
                  <div className='flex gap-5 items-center justify-between'>
                    <label
                      className='mr-8'
                      style={{ fontWeight: 600, color: '#A6ABB6', width: '150px' }}
                    >
                      Username{appView === 'admin' ? '/ Password' : ''}
                    </label>

                    <TextInput
                      data-testid={getHTMLDataTestID('rdp', 'label', 'add-user-aws-username')}
                      disabled={dbRow[i]?.disableInput}
                      value={dbRow[i]?.Username}
                      style={{ width: '200px' }}
                      placeholder='Username'
                    />

                    {!isAdDomainSetupExist && appView === 'admin' && (
                      <TextInput
                        data-testid={getHTMLDataTestID('rdp', 'label', 'add-user-aws-pw')}
                        aria-required
                        style={{ border: dbRow[i].error ? '1px solid red' : '' }}
                        type='password'
                        value={dbRow[i]?.Password}
                        placeholder='Password *'
                      />
                    )}

                    <div className='flex'>
                      <div className='flex gap-1 items-center'>
                        <IconButton
                          disabled={!isPolicyExists}
                          data-testid={getHTMLDataTestID('rdp', 'label', 'add-user-aws-copy')}
                          onClick={() => isPolicyExists && handleCopyCmd(dbRow[i]?.Username)}
                          variant='gray'
                          icon={faCopy}
                        />
                        <Tooltip
                          arrow
                          placement='top-start'
                          title={
                            isPolicyExists
                              ? 'Copy Principal Password'
                              : 'User is not authorized. Create a policy or request access.'
                          }
                        >
                          <IconButton
                            className='scale-75'
                            data-testid={getHTMLDataTestID('rdp', 'label', 'add-user-aws-clear')}
                            onMouseOver={() => {}}
                            variant='clear'
                            icon={faInfo}
                          />
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )
        })}

      <LoadingFeedback
        message={'Updating RDP User Management'}
        loading={isLoading}
        caption='Please wait...'
      />
    </>
  )
}

export { RDPServer }
